'use client';

import { useState } from 'react';
import Link from 'next/link';
import { X, ChevronDown } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';
import { navItems, isDropdown } from '@/data/navigation';
import LanguageSwitcher from '@/components/layout/LanguageSwitcher';
import OverlayDialog from '@/components/ui/OverlayDialog';

export default function MobileMenu({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const t = useTranslations('Nav');
  const [expanded, setExpanded] = useState<string | null>(null);
  const close = () => { setExpanded(null); onClose(); };

  return (
    <OverlayDialog open={isOpen} onClose={close} label="Navigation menu" className="inset-y-0 left-auto right-0 m-0 h-dvh w-[min(100%,24rem)] bg-[#FAFAF5] text-[#1A1A2E]">
      <div className="flex h-full flex-col overflow-y-auto overscroll-contain p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:p-6">
        <div className="mb-4 flex items-center justify-between gap-4">
          <span className="text-lg font-light tracking-wide">Kettura Finland</span>
          <button type="button" onClick={close} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg hover:bg-[#F0EDE5]" aria-label="Close menu">
            <X className="h-6 w-6" />
          </button>
        </div>
        <nav aria-label="Main navigation" className="flex-1">
          {navItems.map((item) => (
            <div key={isDropdown(item) ? item.label : item.href}>
              {isDropdown(item) ? (
                <>
                  <button type="button" onClick={() => setExpanded(expanded === item.label ? null : item.label)} aria-expanded={expanded === item.label} className="flex min-h-12 w-full items-center justify-between gap-3 rounded-lg px-3 py-3 text-left font-sans text-base uppercase tracking-wide hover:bg-[#F0EDE5]">
                    {t(item.tKey)}<ChevronDown className={cn('h-4 w-4 shrink-0 transition-transform', expanded === item.label && 'rotate-180')} />
                  </button>
                  {expanded === item.label && (
                    <div className="border-l border-[#9B7B3A]/30 ml-3 pl-2 pb-2">
                      {item.items.map((subItem) => <Link key={subItem.href} href={subItem.href} onClick={close} className="flex min-h-11 items-center rounded-lg px-3 py-2 text-base text-[#5A5A6E] hover:bg-[#F0EDE5]">{t(subItem.tKey)}</Link>)}
                    </div>
                  )}
                </>
              ) : <Link href={item.href} onClick={close} className="flex min-h-12 items-center rounded-lg px-3 py-3 font-sans text-base uppercase tracking-wide hover:bg-[#F0EDE5]">{t(item.tKey)}</Link>}
            </div>
          ))}
          <Link href="/cabins" onClick={close} className="mt-4 flex min-h-12 items-center justify-center rounded-full bg-[#9B7B3A] px-4 py-3 text-center font-sans font-semibold text-white">{t('bookNow')}</Link>
        </nav>
        <div className="mt-6 border-t border-[#F0EDE5] pt-5"><LanguageSwitcher compact={false} /></div>
      </div>
    </OverlayDialog>
  );
}
